import React, { useEffect, useState } from 'react'
import { Box, Chip, Divider, Typography } from '@mui/material'
import { BugReport } from '@mui/icons-material'
import moment from 'moment'
import TableData from '../common/TableData'
import TxnLogView from '../common/TxnLogView'
import { currencyFormatter } from '../../helpers/util'
import { getSearchTxnReportService } from '../../services/report/reportService'
import { getLogsTxnService } from '../../services/panelService'

// Contenido del dialogo que se abre desde una fila de origen Proveedor en las
// transacciones no conciliadas.
//
// Esa fila no trae identificador de transaccion de HEBE -- si lo trajera estaria
// conciliada --, asi que aqui se buscan las transacciones de ese numero ese dia,
// aprobadas o no, y se muestra el log de la que se elija. La conciliacion solo compara
// aprobadas: lo normal es que la transaccion exista en HEBE y no haya sido aprobada, y
// el log es donde esta la explicacion.
//
// No se empareja automaticamente con ninguna: el codigo de autorizacion del proveedor
// no siempre existe en una transaccion que no se aprobo, y ensenar el log equivocado en
// una conciliacion es peor que pedir un clic mas. Se senala el mismo importe y decide
// quien mira.

const styles = {
  titulo: { fontSize: 12, fontWeight: 'bold', marginBottom: 0.5 },
  nota: { fontSize: 11, color: 'text.secondary' },
  chip: { fontSize: 10, height: 18, marginLeft: 1 }
}

const vacio = (val) => val === null || val === undefined || val === ''

const texto = (val) => (vacio(val) ? '-' : String(val))

const hora = (val) => (vacio(val) ? '-' : moment(val).format('DD/MM/YYYY HH:mm:ss'))

// El importe vive en distintos campos segun el tipo de operacion y segun quien arme el
// documento: el proceso de conciliacion proyecta Amount y el buscador devuelve amount.
const importe = (row) => {
  const valor = ['TAE', 'PDS', 'PIN'].includes(row?.typeReg)
    ? (row?.amount ?? row?.Amount)
    : (row?.operacionSaldo ?? row?.amount ?? row?.Amount)
  return (vacio(valor) || isNaN(valor)) ? null : Number(valor)
}

const dinero = (valor) => (valor === null ? '-' : currencyFormatter.format(valor))

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 140, marginBottom: 1, marginRight: 3 }}>
    <Typography sx={{ fontSize: 10, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, wordBreak: 'break-all' }}>{children}</Typography>
  </Box>
)

const ConciliacionTxnLogView = ({ record, instancia, fallbackDate }) => {
  const [candidatas, setCandidatas] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [seleccionada, setSeleccionada] = useState(null)
  const [log, setLog] = useState(null)
  const [loadingLog, setLoadingLog] = useState(false)

  // La fecha de la fila la arma el proceso de conciliacion con lo que traiga el archivo
  // del proveedor, y no siempre trae fecha: entonces se mira el dia que esta filtrado
  // en la pantalla.
  const propia = !vacio(record?.createdAt) && moment(record.createdAt).isValid() ? moment(record.createdAt) : null
  const filtrada = !vacio(fallbackDate) && moment(fallbackDate).isValid() ? moment(fallbackDate) : null
  const dia = propia || filtrada
  const montoProveedor = importe(record)

  const verLog = (txn) => {
    if (!txn?._id) return
    setSeleccionada(txn)
    setLog(null)
    setLoadingLog(true)
    getLogsTxnService(txn._id)
      .then((resultado) => setLog(resultado))
      .catch((e) => setLog(e?.message || 'No se pudo obtener el log'))
      .finally(() => setLoadingLog(false))
  }

  useEffect(() => {
    if (!dia) {
      setLoading(false)
      setError('No se sabe de qué día es esta transacción: la fila no trae fecha y la pantalla no tiene un día filtrado.')
      return
    }
    if (vacio(record?.phoneNumber)) {
      setLoading(false)
      setError('La fila no trae número: no hay con qué buscar la transacción en HEBE.')
      return
    }
    const from = dia.clone().startOf('day').valueOf()
    const to = dia.clone().endOf('day').valueOf()
    getSearchTxnReportService({ phone: record.phoneNumber, from, to })
      .then((respuesta) => {
        const lista = Array.isArray(respuesta?.data) ? respuesta.data : []
        // Un API anterior a este cambio ignora el rango y responde con su periodo por
        // omision. Quedarse solo con lo que cae en el dia pedido evita ensenar como
        // candidata una transaccion de otro dia.
        const delDia = lista.filter(({ createdAt }) => createdAt >= from && createdAt <= to)
        setCandidatas(delDia)
        if (delDia.length === 1)
          verLog(delDia[0])
      })
      .catch((e) => setError(e?.message || 'No se pudieron buscar las transacciones de HEBE'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line
  }, [record])

  const columns = [
    { label: 'Fecha y hora', format: (row) => hora(row?.createdAt) },
    {
      label: 'Importe',
      format: (row) => {
        const monto = importe(row)
        const coincide = monto !== null && montoProveedor !== null && monto === montoProveedor
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {dinero(monto)}
            {coincide && <Chip size="small" color="info" label="Mismo importe" sx={styles.chip} />}
          </Box>
        )
      }
    },
    { label: 'Estado', format: (row) => texto(row?.ProviderResponseMessage) },
    { label: 'Código', format: (row) => texto(row?.ProviderResponseCode) },
    { label: 'Autorización', format: (row) => texto(row?.AuthCode) },
    { label: 'Comercio', format: (row) => texto(row?.commerce?.username) },
  ]

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography sx={styles.titulo}>Lo que reportó el proveedor</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        <Field label="Instancia">{texto(instancia)}</Field>
        <Field label="Número">{texto(record?.phoneNumber)}</Field>
        <Field label="Importe">{dinero(montoProveedor)}</Field>
        <Field label="Fecha y hora">{hora(record?.createdAt)}</Field>
        <Field label="Autorización">{texto(record?.AuthCode)}</Field>
        <Field label="Código">{texto(record?.ProviderResponseCode)}</Field>
      </Box>

      <Divider sx={{ marginY: 1 }} />
      <Typography sx={styles.titulo}>
        Transacciones de HEBE del {dia ? dia.format('DD/MM/YYYY') : '-'}
        {!propia && filtrada && ' (la fila no trae fecha: se usa el día filtrado en la pantalla)'}
      </Typography>
      <Typography sx={styles.nota}>
        Todas las de este número ese día, aprobadas o no. La conciliación sólo compara las aprobadas,
        así que una transacción declinada o sin respuesta no aparece como conciliada aunque exista.
      </Typography>

      {error
        ? <Typography sx={{ fontSize: 12, color: 'error.main', marginTop: 1 }}>{error}</Typography>
        : <TableData
          loading={loading}
          search={false}
          pagination={false}
          dataRows={candidatas || []}
          dataTitle={columns.map(({ label }) => label)}
          formatter={(data) => columns.map(({ format }) => format(data))}
          actions={[
            {
              label: 'Ver log',
              icon: <BugReport />,
              validate: (data) => Boolean(data?._id),
              onClick: verLog
            }
          ]}
        />}

      {!loading && !error && candidatas?.length === 0 && (
        <Typography sx={{ fontSize: 12, marginTop: 1 }}>
          HEBE no tiene ninguna transacción de este número ese día. Aquí la ausencia sí significa que la
          operación no quedó registrada.
        </Typography>
      )}

      {seleccionada && <>
        <Divider sx={{ marginY: 1 }} />
        <Typography sx={styles.titulo}>Log de la transacción {seleccionada._id}</Typography>
        {loadingLog
          ? <Typography sx={styles.nota}>Cargando el log…</Typography>
          : <TxnLogView data={log} />}
      </>}
    </Box>
  )
}

export default ConciliacionTxnLogView
